import type { ButtonHTMLAttributes, ReactNode } from "react";

type ButtonVariant =
  | "primary"
  | "secondary"
  | "destructive"
  | "toolbar"
  | "carousel"
  | "modal"
  | "modalSecondary";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  variant?: ButtonVariant;
}

const baseStyles =
  "inline-flex items-center justify-center font-medium transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed";

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    gap-2 rounded-lg
    px-3 py-2
    bg-[var(--accent-primary)]
    border border-[var(--accent-primary)]
    text-[var(--text-primary)]
    btn-primary-hover
    btn-primary-disabled
    focus-visible:ring-[var(--accent-primary)]
  `,
  secondary: `
    gap-2 rounded-lg
    px-4 py-2.5
    bg-[var(--accent-secondary)]
    border border-[var(--accent-secondary-stroke)]
    text-[var(--text-primary)]
    hover:bg-[var(--accent-secondary-hover)]
    hover:border-[var(--accent-secondary-hover-stroke)]
    focus-visible:ring-[var(--accent-secondary-stroke)]
  `,
  destructive: `
    gap-2 rounded-lg
    px-4 py-2.5
    bg-[var(--accent-destructive)]
    border border-[var(--stroke-destructive)]
    text-[var(--text-primary)]
    hover:bg-[var(--accent-destructive-hover)]
    focus-visible:ring-[var(--accent-destructive)]
  `,
  toolbar: `
    gap-1
    rounded-[4px]
    px-2 py-1
    bg-[var(--bg-field)]/40
    border border-[var(--stroke-field)]/40
    text-[13px] font-medium
    text-white
    hover:bg-[var(--bg-field)]/60
    aria-pressed:bg-[var(--Buttons-Secondary-Button---Fill)]
    aria-pressed:border-[var(--accent-primary)]
    aria-pressed:hover:bg-[var(--Buttons-Secondary-Button---Fill)]
    focus-visible:ring-[var(--stroke-field)]
    disabled:opacity-50
  `,
  carousel: `
    rounded-lg
    p-3
    bg-[var(--bg-modal)]
    border border-[var(--bg-modal)]
    text-[var(--text-primary)]
    focus-visible:ring-[var(--bg-modal)]
  `,
  modal: `
    h-10
    rounded-[4px]
    px-4 py-3
    bg-[var(--btn-modal-primary)]
    border border-[var(--btn-modal-primary)]
    text-sm font-semibold
    text-[var(--text-primary)]
    hover:opacity-90
    disabled:opacity-50
    focus-visible:ring-[var(--btn-modal-primary)]
  `,
  modalSecondary: `
    h-10
    rounded-[4px]
    px-4 py-3
    bg-[var(--Buttons-Secondary-Button---Fill)]
    border border-[var(--Buttons-Secondary-Button---Stroke)]
    text-sm font-semibold
    text-[var(--text-primary)]
    hover:bg-[rgba(60,124,165,0.4)]
    disabled:opacity-50
    focus-visible:ring-[var(--Buttons-Secondary-Button---Stroke)]
  `,
};

export function Button({
  children,
  variant = "primary",
  className = "",
  disabled = false,
  type = "button",
  ...props
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled}
      className={`${baseStyles} ${variantStyles[variant]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
}

/** Primary action of a modal footer. */
export function ModalPrimaryButton(
  props: Omit<ButtonProps, "variant" | "className">,
) {
  return <Button variant="modal" {...props} />;
}

/** Dismissive action sitting beside a <ModalPrimaryButton>. */
export function ModalSecondaryButton(
  props: Omit<ButtonProps, "variant" | "className">,
) {
  return <Button variant="modalSecondary" {...props} />;
}
